import { useState } from 'react';
import { LuX, LuBuilding2, LuUsers } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useBuilding } from '../../contexts/BuildingContext';
import { toast } from '../../hooks/use-toast';
import MentionsAutocomplete from './MentionsAutocomplete';
import { createMentionNotification } from '../../utils/notificationHelpers';
import { extractMentionNames, validateMentionedUsers, storeMentions } from '../../utils/mentionUtils';

export default function CreateTopicForm({ defaultScope, onClose, onSuccess }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentBuilding, currentProperty } = useBuilding();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    scope: defaultScope || 'building_specific',
    title: '',
    body: '',
    category: 'general',
    tags: ''
  });

  const categories = [
  { value: 'general', label: t('forum.categories.general') },
  { value: 'maintenance', label: t('forum.categories.maintenance') },
  { value: 'events', label: t('forum.categories.events') },
  { value: 'rules', label: t('forum.categories.rules') },
  { value: 'suggestions', label: t('forum.categories.suggestions') }];



  const handleMentions = async (text, topicId, topicTitle) => {
    try {
      const usernames = extractMentionNames(text);
      if (usernames.length === 0) return;

      const validUsers = await validateMentionedUsers(usernames);
      if (validUsers.length === 0) return;

      // Store mentions in database
      await storeMentions(validUsers, user.id, 'topic', topicId);

      // Send notifications
      for (const mentionedUser of validUsers) {
        if (Number(mentionedUser.id) === Number(user.id)) continue;

        await createMentionNotification(
          mentionedUser.id,
          user.name,
          'topic',
          topicId,
          topicTitle,
          text
        ).catch((e) => console.error('Failed to send mention notification:', e));
      }
    } catch (e) {
      console.error('[Mentions] Error processing mentions:', e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.body.trim()) {
      toast({
        title: t('common.error'),
        description: t('forum.validationError'),
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const now = new Date().toISOString();
      const { data: topicData, error: topicError } = await window.ezsite.apis.tableCreate(70652, {
        scope: formData.scope,
        building_id: formData.scope === 'building_specific' ? currentBuilding?.id : null,
        property_id: currentProperty?.id,
        title: formData.title,
        body: formData.body,
        author_id: user.id,
        category: formData.category,
        tags: formData.tags,
        is_pinned: false,
        is_locked: false,
        reply_count: 0,
        last_activity_at: now,
        created_at: now,
        updated_at: now
      });

      if (topicError) throw topicError;

      // Handle mentions after topic creation
      // Extract topic ID - API may return it in different formats
      let topicId = null;
      if (typeof topicData === 'number') {
        topicId = topicData;
      } else if (topicData?.id) {
        topicId = topicData.id;
      } else if (topicData?.ID) {
        topicId = topicData.ID;
      }

      if (topicId) {
        await handleMentions(formData.body, topicId, formData.title);
      } else {
        console.warn('[CreateTopic] Could not extract topic ID for mentions:', topicData);
      }

      toast({ title: t('common.success'), description: t('forum.createSuccess') });
      onSuccess();
    } catch (error) {
      console.error('Failed to create topic:', error);
      toast({
        title: t('common.error'),
        description: t('forum.createFailed'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-in zoom-in duration-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 sticky top-0 bg-white/80 backdrop-blur-md z-10">
          <h2 className="text-2xl font-bold text-gray-900">{t('forum.createNewTopic')}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <LuX className="w-6 h-6 text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-3">{t('forum.topicScope')}</label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, scope: 'building_specific' })}
                className={`flex items-center gap-3 p-4 border-2 rounded-xl transition-all ${formData.scope === 'building_specific' ? 'border-blue-600 bg-blue-50 shadow-sm' : 'border-gray-100 hover:border-gray-200'}`}>

                <div className={`p-2 rounded-lg ${formData.scope === 'building_specific' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                  <LuUsers className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <p className="font-bold text-gray-900 text-sm">{t('forum.myBuilding')}</p>
                  <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">{t('forum.buildingOnly')}</p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, scope: 'property_wide' })}
                className={`flex items-center gap-3 p-4 border-2 rounded-xl transition-all ${formData.scope === 'property_wide' ? 'border-purple-600 bg-purple-50 shadow-sm' : 'border-gray-100 hover:border-gray-200'}`}>

                <div className={`p-2 rounded-lg ${formData.scope === 'property_wide' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                  <LuBuilding2 className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <p className="font-bold text-gray-900 text-sm">{t('forum.propertyWide')}</p>
                  <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">{t('forum.propertyOnly')}</p>
                </div>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">{t('forum.topicTitle')}</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder={t('forum.titlePlaceholder')}
              className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all"
              required />

          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">{t('forum.category')}</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all appearance-none cursor-pointer">

                {categories.map((cat) =>
                <option key={cat.value} value={cat.value}>{cat.label}</option>
                )}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">{t('forum.tags')}</label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder={t('forum.tagsPlaceholder')}
                className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 focus:border-blue-500 outline-none transition-all" />

            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">{t('forum.messageContent')}</label>
            <MentionsAutocomplete
              value={formData.body}
              onChange={(value) => setFormData({ ...formData, body: value })}
              placeholder={t('forum.messagePlaceholder')}
              rows={8} />

          </div>

          <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-gray-500 font-bold hover:bg-gray-50 rounded-xl transition-all">

              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 shadow-lg shadow-blue-100 transition-all disabled:opacity-50 active:scale-95">

              {loading ? t('forum.creating') : t('forum.postTopic')}
            </button>
          </div>
        </form>
      </div>
    </div>);

}